/**
 * Star Rating Card
 *
 * This code defines a React component called CardStarRating that renders a star rating 
 * form for user feedback. The component includes a form with a star rating input, 
 * a comment input, and a submit button. When the form is submitted, the user's feedback 
 * is sent to the API endpoint. The component also includes conditional rendering logic to 
 * display a success message when feedback is submitted and to hide a specific image when 
 * the hunt template ID is 707.
 * 
 * Shown on the Finish Page.
 */

import React from 'react'
import { isPermanentCode } from '../utils/team-data'
import { getCurrentHuntData } from '../utils/hunt-data'
import Image from './image'
import SVGLoader from './svg/loader'
import { getCurrentUser } from '../utils/auth'
import API from '../utils/api'
import StarRatings from 'react-star-ratings'

export default class CardStarRating extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      huntData: getCurrentHuntData(),
      isPermanent: isPermanentCode(),
      feedback_type: 3, // 3 is rating type
      rating: 0,
      comment: ``,
      feedbackSubmitted: false,
      loading: false,
      errors: ``,
    }
    this.handleUpdate = this.handleUpdate.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.ratingChanged = this.ratingChanged.bind(this)
  }

  handleSubmit(event) {
    event.preventDefault()
    this.setState({ loading: true })
    let hunt_data = getCurrentHuntData()
    const { name, guid } = getCurrentUser()

    if (this.state.rating == 0) {
      this.setState({
        loading: false,
        errors: `Please select a rating.`,
      })
      return
    }

    const self = this

    const post_data = new FormData()
    post_data.append(`feedback_type`, this.state.feedback_type)
    post_data.append(`comment`, this.state.comment)
    post_data.append(`stars`, this.state.rating)
    post_data.append(`hunt_id`, hunt_data.hunt.id)
    post_data.append(`name`, name)
    post_data.append(`guid`, guid)

    API.post(`feedback/`, post_data)
      .then(res => {
        console.log(res)
        this.setState({
          feedbackSubmitted: true,
          loading: false,
          comment: ``,
          rating: 0,
          errors: ``,
        })
        this.props.nextFunc()
      })
      .catch(function(error) {
        console.log(error)
        self.setState({ loading: false })
      })
  }

  handleUpdate(event) {
    this.setState({
      [event.target.name]: event.target.value,
    })
  }

  ratingChanged(newRating) {
    this.setState({ rating: newRating })
    if (newRating > 0) {
      this.setState({ errors: `` })
    }
  }

  render() {
    const { loading, huntData, feedbackSubmitted, errors } = this.state
    console.log(huntData)

    return (
      <>
        <div className="card">
          <div className="card-header text-center">
            <h3>How Would you rate your hunt?</h3>
          </div>
          <div className="card-body text-center justify-content-between">
            {feedbackSubmitted ? (
              <>
                <div style={{ maxWidth: `125px`, width: `125px` }} className={`mb-4 text-center mx-auto`}>
                  <Image alt="Hands clapping" filename="hands_clap.png" />
                </div>

                <p
                  className={`text-center`}
                  style={{
                    fontFamily: `"ff-good-web-pro-extra-conden", GoodPro-XCondBlack, sans-serif`,
                    textTransform: `uppercase`,
                    fontWeight: `800`,
                    fontSize: `23px`,
                    color: `#273A41`,
                    letterSpacing: `2.04px`,
                    lineHeight: `28px`,
                  }}>
                  Thanks for your feedback!
                </p>
              </>
            ) : (
              <>
                {
                  // Remove telescope man from Princeton games where the hunt template ID is 707
                }
                {huntData[`hunt`][`hunt_template_id`] !== 707 && (
                  <div className="mt-3 mb-5">
                    <div
                      style={{
                        maxWidth: `72px`,
                        marginLeft: `auto`,
                        marginRight: `auto`,
                      }}>
                      <Image alt="Site Feedback" filename="bin-rating.png" />
                    </div>
                  </div>
                )}
                <form
                  method="post"
                  onSubmit={event => {
                    this.handleSubmit(event)
                  }}>
                  <div className={`mb-3`}>
                    <div className={`d-inline-block mx-auto`}>
                      <StarRatings
                        rating={this.state.rating}
                        changeRating={this.ratingChanged}
                        numberOfStars={5}
                        name="rating"
                        starEmptyColor="rgb(228,219,202)"
                        starRatedColor="rgb(202,152,49)"
                        starHoverColor="rgb(202,152,49)"
                        starDimension="35px"
                        starSpacing="0px"
                        svgIconPath="M8.352 22.107c.18.178.263.431.22.681L6.79 33.795c-.105.634.552 1.11 1.106.806l9.656-5.278a.749.749 0 0 1 .71-.006l9.716 5.096c.559.293 1.207-.196 1.098-.827l-1.914-10.97a.778.778 0 0 1 .213-.689l7.791-7.854c.448-.453.197-1.231-.427-1.315L23.9 11.254a.753.753 0 0 1-.576-.415L18.42.885a.756.756 0 0 0-1.364.012l-4.784 10.044a.765.765 0 0 1-.57.429L.883 13.074c-.622.1-.867.88-.412 1.324l7.88 7.71Z"
                        svgIconViewBox="0 0 35 36"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    {errors.length != 0 ? (
                      <div className={`errors text-danger`}>{errors}</div>
                    ) : (
                      <span>Tap to rate</span>
                    )}
                  </div>

                  <div className={`form-group border-bottom-0`}>
                    <label className={`sr-only`} htmlFor="comment">
                      comment
                    </label>
                    <textarea
                      type="text"
                      rows="5"
                      name="comment"
                      className={`form-control form-control-textarea px-4 py-3`}
                      value={this.state.comment}
                      onChange={this.handleUpdate}
                      placeholder="Tell us why! (Optional)"
                      style={{
                        background: `#EEEAE1`,
                        borderBottom: `1px solid #273A41`,
                      }}
                    />
                  </div>
                  {loading ? (
                    <p className={`text-center pt-4`}>
                      <SVGLoader className={`text-primary`} />
                    </p>
                  ) : (
                    <input className={`btn btn-block btn-secondary`} type="submit" value="Submit" />
                  )}
                </form>
              </>
            )}
          </div>
        </div>
      </>
    )
  }
}
